<template>
  <div ref="listRef" class="template-wrapper">
    <div class="container">
      <div class="sticky top-0 z-[11] bg-back-1 pt-[30px] -mx-[60px] px-[60px] filter-box">
        <div class="tag-list">
          <div v-for="item in availableTags" :key="item.tag_id" class="flex items-start">
            <div
              class="min-w-[4em] text-16 font-semibold mr-[23px] whitespace-nowrap p-[3px_0] text-right text-font-1"
            >
              {{ item?.tag_name }}
            </div>
            <div
              class="flex-y-center flex-wrap gap-[10px_14px] text-14 text-font-60 select-none cursor-pointer"
            >
              <NuxtLink
                v-for="x in getTagList(item)"
                :key="x.tag_id"
                :href="getLink(item, x)"
                class="dict-item"
                :class="{ active: getActive(x, item) }"
              >
                {{ x.tag_name }}
              </NuxtLink>
            </div>
          </div>
        </div>
        <div class="w-full flex-y-center pb-[5px]">
          <DictFilter
            v-model="searchFrom.sort"
            name="sorts"
            :has-all="false"
            class="text-14 gap-x-[20px]"
          />
          <div class="justify-end flex-1 flex-y-center">
            <div class="flex-y-center">
              <span class="dict-name line">{{ $t('ban-shi') }}</span>
              <DictFilter
                v-model="searchFrom.format"
                name="formats"
                class="format text-14 gap-x-[20px]"
                @change="onChangeFormat"
              />
            </div>
            <div class="flex-y-center ml-[30px]">
              <span class="dict-name -mr-[15px]">
                {{ $t('yan-bo-shi-lei-xing') }}
              </span>
              <a-select
                v-model:value="searchFrom.map_types"
                :placeholder="$t('qing-xuan-ze')"
                size="small"
                class="w-[120px] dict-select"
                allow-clear
                :options="studioListFilter"
                @change="onChangeStudio"
              >
                <template #suffixIcon>
                  <iconpark-icon name="arrowdown" class="opacity-30" />
                </template>
              </a-select>
            </div>
          </div>
        </div>
      </div>
      <a-alert
        v-if="sceneTip"
        :message="sceneTip"
        type="info"
        show-icon
        closable
        :after-close="onCloseEnd"
      />

      <a-spin class="py-[15px]" :spinning="pending">
        <TemplateMasonry
          v-if="calList.length"
          :break-points="{
            '0': 4,
            '1500': 5,
          }"
          :list="calList"
          class="gap-y-[0px]"
          :template_source="TEMPLATE_SOURCE.TEMPLATE_PAGE"
          @card-click="onHandleClick"
        />
        <EmptyView v-else-if="!pending && !first" />
      </a-spin>

      <div class="flex-center">
        <a-pagination
          v-model:current="current"
          class="m-[50px] mx-auto flex-right"
          :total="total"
          :page-size="PAGE_SIZE"
          @change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SOURCE, TEMPLATE_SOURCE } from '@/utils/track';
import { debounce } from 'lodash-es';
import { getStudioList, getTemplate, getTemplateList } from '~/api';
import type { Tag, Template } from '~/api/interface';
import TemplateMasonry from '~/components/templates/index.vue';
import { TEMPLATE_SORT } from '~/constants';
import { useTemplateDetail } from '~/hooks/useModal/modals/template';
import { useMountedWatch } from '~/hooks/useMountedWatch';
import { usePaginatedList } from '~/hooks/usePaginatedList';
import { SCROLL_LIST } from '~/hooks/useScrollList';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useStarPatchList } from '~/hooks/useStarPatch';

const PAGE_SIZE = 100;

const route = useRoute();
const { $t } = useNuxtApp();

const fmt = (v: string | undefined) => (v ? v.replaceAll('_', '-') : v);
const rvt = (v: string | undefined) => (v ? v.replaceAll('-', '_') : v);

const getTagList = (item: Tag) => {
  if (item.outer_id === 'template_application_scene') {
    return [
      {
        tag_id: 'all',
        outer_id: 'all',
        tag_name: $t('quan-bu'),
        ids: '',
        level: 1,
      },
      ...(item.children || []),
    ];
  }
  return item.children;
};

const getLink = (item: Tag, sub: Tag) => {
  let base = '/templates';
  if (item.outer_id === 'industry') {
    base += `/${fmt(sub.outer_id)}`;
    return base;
  }

  if (item.outer_id === 'template_application_scene') {
    if (sub.outer_id === 'all') {
      return `${base}/${fmt(currentIndustry.value?.outer_id)}`;
    }
    return `${base}/${fmt(currentIndustry.value?.outer_id)}/${fmt(sub.outer_id)}`;
  }
};

const getActive = (sub: Tag, item: Tag) => {
  if (!route.params.scene && sub.outer_id === 'all') {
    return true;
  }

  if (item.outer_id === 'industry') {
    return rvt(route.params.industry as string) === sub.outer_id;
  }

  if (item.outer_id === 'template_application_scene') {
    return rvt(route.params.scene as string) === sub.outer_id;
  }

  return false;
};

const onHandleClick = (data: Template) => {
  navigateTo(`/template/${data.id}.html`);
};

const centerTags = useCenterTags();

const availableTags = computed(() => {
  return centerTags.value.tags
    .filter(v => v.outer_id && ['industry', 'template_application_scene'].includes(v.outer_id))
    .map(v => {
      if (v.outer_id === 'template_application_scene') {
        v.children?.forEach(item => {
          item.ids = item.children!.map(v => v.ids).join('_');
        });
      }
      return v;
    });
});

const currentIndustry = computed(() => {
  const industries = centerTags.value.tags.find(v => v.outer_id === 'industry');
  return industries?.children?.find(v => v.outer_id === rvt(route.params.industry as string));
});

const currentScene = computed(() => {
  if (!route.params.scene) {
    return null;
  }
  const scenes = centerTags.value.tags.find(v => v.outer_id === 'template_application_scene');
  return scenes?.children?.find(v => v.outer_id === rvt(route.params.scene as string));
});

const seoData = computed(() => {
  const industryName = currentIndustry.value?.tag_name;
  const sceneName = currentScene.value?.tag_name;
  return {
    seo_title: $t('templates-seo-title', [industryName, sceneName]),
    seo_description: $t('templates-seo-description', [industryName, sceneName]),
    seo_keywords: `${industryName},${sceneName}`,
  };
});

useWatchPageSeoData(seoData);

const searchFrom = reactive<any>({
  sort: TEMPLATE_SORT.NEW,
  format: 'all',
  map_types: route.query.map_types || undefined,
  tag_ids: [currentIndustry.value?.ids, currentScene.value?.ids].filter(Boolean).join('_'),
});

const listRef = ref();
const studioList = ref<any[]>([]);
const sceneTip = ref('');
const KEYNAME = 'youyan-studio-keys';

const studioListFilter = computed(() => {
  return studioList.value.filter(x => {
    const l = x.resolutions;
    if (searchFrom.format === 'all' || !l || l.length === 0) {
      return true;
    }

    return l.includes(searchFrom.format);
  });
});

provideResourceLocation({
  source: SOURCE.TEMPLATE_CENTER,
});

const { list, pending, refresh, next, total, current } = usePaginatedList(
  async params => {
    const res = await getTemplateList({
      ...params,
      tag_ids: searchFrom.tag_ids,
      o: searchFrom.sort,
      is_template_center_template: 1,
      map_types: searchFrom.map_types as string,
      resolution: searchFrom.format !== 'all' ? searchFrom.format : undefined,
      platform: 0,
      online_status: 0,
      query_star: import.meta.client,
    });

    res.data?.results?.forEach(item => {
      item.query_star = import.meta.client; // 标记这些数据是否查询过star
    });
    return res;
  },
  {
    page: +route.params.page || 1,
    pageSize: PAGE_SIZE,
    key: SCROLL_LIST.TEMPLATE_LIST,
  },
);

const handlePageChange = async (page: number) => {
  await next(page);
  const scene = fmt(currentScene.value?.outer_id) || 'all';

  let path = `/templates/${fmt(currentIndustry.value?.outer_id)}`;
  if (scene !== 'all') {
    path = `${path}/${scene}`;
  }

  history.pushState({}, '', path);
  nextTick(() => {
    listRef.value?.scrollTo(0, 0);
  });
};

const { calList, status } = useStarPatchList(list, 2);

let first = true;

useMountedWatch([calList, status], async () => {
  if (first && status.value === 'success' && route.query.id) {
    first = false;
    const index = calList.value.findIndex((x: any) => x.id === Number(route.query.id)) || -1;
    if (index > -1) {
      setTimeout(() => {
        listRef.value.querySelector(`.card-item:nth-child(${index + 1})`)?.click();
      }, 100);
    } else {
      const details = await getTemplate({
        template_id: Number(route.query.id),
      });
      if (details.code === 0) {
        useTemplateDetail(
          [details.data!],
          details.data!,
          {
            templateSource: TEMPLATE_SOURCE.TEMPLATE_PAGE,
            source: SOURCE.TEMPLATE_CENTER,
          },
          undefined,
        );
      }
    }
  }
});

useBackToTop(() => ({
  scrollContainer: '.template-wrapper',
  item: '.card-item',
  total: total.value,
}));

const getStudioData = async () => {
  const res = await getStudioList();
  if (res.code !== 0) {
    return;
  }

  studioList.value = !res.data
    ? []
    : res.data.map((item: { resolutions: any; message: any; name: any; id: any }) => ({
        value: String(item.id),
        label: item.name,
        message: item.message,
        resolutions: item.resolutions,
      }));
};

getStudioData();

const onChangeStudio: any = (v: string, d: any) => {
  const keys = JSON.parse(localStorage.getItem(KEYNAME) || '{}');
  sceneTip.value = '';
  if (v && !keys[v] && d.message) {
    sceneTip.value = `${d.label}：“${d.message}”`;
  }
};
const onCloseEnd = () => {
  sceneTip.value = '';
  const keys = JSON.parse(localStorage.getItem(KEYNAME) || '{}');
  localStorage.setItem(KEYNAME, JSON.stringify({ ...keys, [searchFrom.map_types]: true }));
};
const onChangeFormat = () => {
  searchFrom.map_types = undefined;
  sceneTip.value = '';
};
watch(
  searchFrom,
  debounce(() => {
    if (listRef.value) {
      listRef.value.scrollTop = 0;
    }

    refresh();
  }, 500),
);
</script>

<style scoped lang="scss">
.sorts {
  @apply font-semibold;
}

.dict-name {
  @apply text-14 flex-y-center;

  &:after {
    @apply ml-[10px] mr-[20px] w-[1px] h-[12px] block;
    content: '';
  }

  &.line:after {
    @apply bg-back-6;
  }
}

.tag-list {
  @apply w-full flex gap-[20px] flex-wrap mb-[20px] flex-col;
  &.small {
    @apply flex-row;
    :deep(.dict-item) {
      display: none;
      &.active {
        display: flex;
      }
    }
  }
}
.filter-box {
  &:hover {
    .tag-list.small {
      @apply flex-col;
      :deep(.dict-item) {
        display: flex;
      }
    }
  }
}

.template-wrapper {
  @apply flex flex-col overflow-y-scroll relative w-full;
  height: calc(100vh - 56px);

  .container {
    width: rem(1200);
    margin: 0 auto;
    min-width: 1200px;
  }
}

.dict-select {
  :deep(.ant-select-selector) {
    height: 28px !important;
    border-radius: 6px;
    .ant-select-selection-placeholder {
      line-height: 25px;
      color: var(--text-1-40);
    }
    .ant-select-selection-item {
      line-height: 25px;
    }
  }
}

.dict-item {
  @apply p-[4px_10px] rounded-[6px] text-font-60 flex-y-center relative hover:text-primary;

  .item-link {
    color: var(--text-1-60);
  }

  &.active {
    @apply font-semibold text-primary bg-back-2;

    .item-link {
      @apply font-semibold text-primary bg-back-2;
    }
  }
  .close-icon {
    @apply absolute -top-[6px] right-[-6px] text-back-6 hidden;
  }
  &:hover {
    .close-icon {
      @apply block;
    }
  }
}

.check-tag {
  @apply p-[4px_10px] rounded-[6px] leading-[1em] bg-back-1;
}
.item-menu {
  &:hover .check,
  .ant-checkbox-wrapper-checked {
    @apply text-primary;
  }
}
</style>
